import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';
import type { PublicOrganizationProfile } from '@/services/publicOrg';

interface OrgPublicProfileContextValue {
  profile: PublicOrganizationProfile | null;
  isLoading: boolean;
  error: string | null;
  orgSlug: string | undefined;
}

const OrgPublicProfileContext = createContext<OrgPublicProfileContextValue | null>(null);

export function OrgPublicProfileProvider({ children }: { children: React.ReactNode }) {
  const { orgSlug: orgSlugParam } = useParams<{ orgSlug?: string }>();
  const orgSlug = useOrgSlug() ?? orgSlugParam;
  const { host, organization: hostOrg } = useOrgHost();

  const seed = useMemo(() => {
    if (host.mode !== 'subdomain' || !hostOrg) return undefined;
    return {
      organization: {
        id: hostOrg.id,
        name: hostOrg.name,
        slug: hostOrg.slug,
        description: hostOrg.description ?? null,
        logoUrl: hostOrg.logoUrl ?? null,
        bannerUrl: hostOrg.bannerUrl ?? null,
        galleryUrls: hostOrg.galleryUrls ?? [],
        address: hostOrg.address ?? null,
        phone: hostOrg.phone ?? null,
        email: hostOrg.email ?? null,
        website: hostOrg.website ?? null,
        facebook: hostOrg.facebook ?? null,
        instagram: hostOrg.instagram ?? null,
        twitter: hostOrg.twitter ?? null,
        linkedin: hostOrg.linkedin ?? null,
        primaryColor: hostOrg.primaryColor ?? null,
        secondaryColor: hostOrg.secondaryColor ?? null,
        country: hostOrg.country ?? null,
        region: hostOrg.region ?? null,
        stateOrProvince: hostOrg.stateOrProvince ?? null,
        timezone: hostOrg.timezone ?? null,
        googleMapsPlaceId: hostOrg.googleMapsPlaceId ?? null,
        googleMapsAddress: hostOrg.googleMapsAddress ?? null,
        showCoachesOnPublicPage: hostOrg.showCoachesOnPublicPage ?? true,
        websiteEmbedEnabled: hostOrg.websiteEmbedEnabled ?? false,
        subdomain: hostOrg.subdomain,
        subdomainEnabled: hostOrg.subdomainEnabled,
      },
      modules: {
        hasAcademy: false,
        hasClub: false,
        courtHireEntitled: false,
        membershipsEntitled: false,
        membershipOnlineJoinEnabled: false,
        playEventsEntitled: false,
      },
      academyName: null,
      clubName: null,
      academy: null,
      academyId: null,
      academySlug: null,
      clubId: null,
      clubSlug: null,
      amenities: hostOrg.amenities ?? [],
      sports: [],
      locations: [],
      operatingHours: null,
    } satisfies PublicOrganizationProfile;
  }, [host.mode, hostOrg]);

  const { profile, isLoading, error } = usePublicOrgProfile({
    slug: host.mode === 'subdomain' ? undefined : orgSlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    seed,
    enabled: !!orgSlug || (host.mode === 'subdomain' && !!host.subdomainLabel),
  });

  const value = useMemo(
    () => ({
      profile,
      isLoading,
      error,
      orgSlug,
    }),
    [profile, isLoading, error, orgSlug]
  );

  return <OrgPublicProfileContext.Provider value={value}>{children}</OrgPublicProfileContext.Provider>;
}

export function useOrgPublicProfile(): OrgPublicProfileContextValue {
  const ctx = useContext(OrgPublicProfileContext);
  if (!ctx) {
    return {
      profile: null,
      isLoading: false,
      error: null,
      orgSlug: undefined,
    };
  }
  return ctx;
}
